import { Box, Chip, Skeleton, Typography, useTheme } from "@mui/material";
import { tokens } from "../theme";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import PointOfSaleIcon from "@mui/icons-material/PointOfSale";
import PaidIcon from "@mui/icons-material/Paid";
import LocalOfferOutlinedIcon from "@mui/icons-material/LocalOfferOutlined";
import Groups2OutlinedIcon from "@mui/icons-material/Groups2Outlined";
import StarsRoundedIcon from "@mui/icons-material/StarsRounded";
import TrendingUpRoundedIcon from "@mui/icons-material/TrendingUpRounded";
import Header from "../Components/Header";
import StatBox from "../Components/StatBox";
import ProgressCircle from "../Components/ProgressCircle";
import LineChart from "../Components/LineChart";
import axios from "axios";
import { useState, useEffect, useMemo } from "react";
import { toast } from "react-toastify";
import Cookies from "js-cookie";
import baseURL from "../config";
import useMediaQuery from "@mui/material/useMediaQuery";
import CryptoJS from "crypto-js";
import { useNavigate } from "react-router-dom";

const numberFormatter = new Intl.NumberFormat("en-US");
const currencyFormatter = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "EUR",
  maximumFractionDigits: 2,
});

const asArray = (value) => (Array.isArray(value) ? value : []);
const asNumber = (value) => {
  const nextValue = Number(value);
  return Number.isFinite(nextValue) ? nextValue : 0;
};

const formatDate = (value) => {
  if (!value) return "N/A";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "N/A" : date.toLocaleDateString();
};

const isDateActive = (startDate, endDate) => {
  const now = Date.now();
  const start = startDate ? new Date(startDate).getTime() : null;
  const end = endDate ? new Date(endDate).getTime() : null;

  if (Number.isNaN(start) || Number.isNaN(end)) return false;
  if (start && now < start) return false;
  if (end && now > end) return false;
  return true;
};

const Dashboard = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const navigate = useNavigate();
  const [userData, setUserData] = useState([]);
  const [posData, setPosData] = useState([]);
  const [transactionsData, setTransactionsData] = useState([]);
  const [offersData, setOffersData] = useState([]);
  const [levelsData, setLevelsData] = useState([]);
  const [customerGroupsData, setCustomerGroupsData] = useState([]);
  const [pointScoresData, setPointScoresData] = useState([]);
  const [lineChartData, setLineChartData] = useState([]);
  const isNonMobile = useMediaQuery("(min-width:839px)");
  const [isLoading, setIsLoading] = useState(true);

  const getDecryptedToken = () => {
    const encryptedToken = Cookies.get("token");
    if (!encryptedToken) return null;

    const secretKey = "19092002";
    const bytes = CryptoJS.AES.decrypt(encryptedToken, secretKey);
    const decryptedToken = bytes.toString(CryptoJS.enc.Utf8);

    return decryptedToken || null;
  };

  const token = getDecryptedToken();
  const authHeaders = token ? { Authorization: `Bearer ${token}` } : {};

  const fetchCoreDashboardData = async () => {
    setIsLoading(true);

    try {
      const [clientsResponse, transactionsResponse, posResponse] = await Promise.all([
        axios.get(`${baseURL}/api/User/GetUsers`, {
          headers: authHeaders,
        }),
        axios.get(`${baseURL}/api/Transaction/GetTransactions`, {
          headers: authHeaders,
        }),
        axios.get(`${baseURL}/api/PointOfSale/GetPOS`, {
          headers: authHeaders,
        }),
      ]);

      if (clientsResponse.data.success) {
        setUserData(asArray(clientsResponse.data.users));
      }

      if (
        transactionsResponse.status === 200 &&
        transactionsResponse.data.message !== "Nuk u gjet asnje transaksion!"
      ) {
        setTransactionsData(asArray(transactionsResponse.data.message));
      }

      if (posResponse.status === 200) {
        setPosData(asArray(posResponse.data));
      }
    } catch (error) {
      toast.error(`Error fetching dashboard data: ${error.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchProgramData = async () => {
    const requests = [
      axios.get(`${baseURL}/api/Offers/GetAllOffers`, {
        headers: authHeaders,
      }),
      axios.get(`${baseURL}/api/Level/GetAllLevels`, {
        headers: authHeaders,
      }),
      axios.get(`${baseURL}/api/CustomerGroup/GetCustomerGroups`),
      axios.get(`${baseURL}/api/PointScore/GetAllPointScore`, {
        headers: authHeaders,
      }),
    ];

    const [offersResponse, levelsResponse, groupsResponse, pointScoresResponse] =
      await Promise.allSettled(requests);

    if (offersResponse.status === "fulfilled" && offersResponse.value.data.success) {
      setOffersData(asArray(offersResponse.value.data.offers));
    }

    if (levelsResponse.status === "fulfilled") {
      setLevelsData(asArray(levelsResponse.value.data));
    }

    if (groupsResponse.status === "fulfilled") {
      setCustomerGroupsData(asArray(groupsResponse.value.data));
    }

    if (
      pointScoresResponse.status === "fulfilled" &&
      pointScoresResponse.value.data.success
    ) {
      setPointScoresData(asArray(pointScoresResponse.value.data.message));
    }
  };

  const processLineChartData = () => {
    if (!Array.isArray(transactionsData) || transactionsData.length === 0) {
      setLineChartData([]);
      return;
    }

    const groupedData = {};

    transactionsData.forEach((transaction) => {
      const date = new Date(transaction.createDate);

      if (Number.isNaN(date.getTime())) {
        return;
      }

      const formattedDate = date.toISOString().split("T")[0];

      if (!groupedData[formattedDate]) {
        groupedData[formattedDate] = {
          count: 0,
          points: 0,
        };
      }

      groupedData[formattedDate].count++;
      groupedData[formattedDate].points += asNumber(transaction.bonusPoints);
    });

    const chartData = [
      {
        id: "Transactions",
        color: colors.primary[100],
        data: Object.entries(groupedData).map(([date, { count }]) => ({
          x: date,
          y: count,
          label: `${count} Transactions`,
        })),
      },
      {
        id: "Points",
        color: colors.greenAccent[500],
        data: Object.entries(groupedData).map(([date, { points }]) => ({
          x: date,
          y: points,
          label: `${points} BonusPoints`,
        })),
      },
    ];

    setLineChartData(chartData);
  };

  useEffect(() => {
    fetchCoreDashboardData();
    fetchProgramData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    processLineChartData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [transactionsData]);

  const dashboardStats = useMemo(() => {
    const totalPoints = transactionsData.reduce(
      (sum, transaction) => sum + asNumber(transaction.bonusPoints),
      0
    );
    const totalAmount = transactionsData.reduce(
      (sum, transaction) =>
        sum +
        asNumber(
          transaction.transactionAmount ??
            transaction.amount ??
            transaction.totalAmount ??
            transaction.price
        ),
      0
    );
    const activeUsers = userData.filter((user) => user.isActive === true).length;
    const activeOffers = offersData.filter((offer) =>
      isDateActive(offer.startDate, offer.endDate)
    ).length;
    const activePointValue =
      pointScoresData.find((pointScore) => pointScore.isActive)?.euro ??
      pointScoresData[0]?.euro ??
      0;

    return {
      activeUsers,
      activeOffers,
      totalAmount,
      totalPoints,
      activePointValue,
    };
  }, [userData, transactionsData, offersData, pointScoresData]);

  const recentTransactions = useMemo(() => {
    return [...transactionsData]
      .sort((a, b) => new Date(b.createDate) - new Date(a.createDate))
      .slice(0, 6);
  }, [transactionsData]);

  const topStores = useMemo(() => {
    const stores = transactionsData.reduce((acc, transaction) => {
      const storeName =
        transaction.store ||
        transaction.storeName ||
        transaction.pointOfSaleName ||
        transaction.posName ||
        `POS #${transaction.posid || transaction.posId || "N/A"}`;

      if (!acc[storeName]) {
        acc[storeName] = {
          name: storeName,
          count: 0,
          points: 0,
          amount: 0,
        };
      }

      acc[storeName].count++;
      acc[storeName].points += asNumber(transaction.bonusPoints);
      acc[storeName].amount += asNumber(
        transaction.transactionAmount ??
          transaction.amount ??
          transaction.totalAmount ??
          transaction.price
      );
      return acc;
    }, {});

    return Object.values(stores)
      .sort((a, b) => b.count - a.count)
      .slice(0, 5);
  }, [transactionsData]);

  const panelSx = {
    backgroundColor: theme.palette.background.paper,
    border: `1px solid ${
      theme.palette.mode === "dark" ? "rgba(255,255,255,0.08)" : "rgba(17,19,24,0.08)"
    }`,
    borderRadius: "14px",
    transition: "border-color 160ms ease, transform 160ms ease",
    "&:hover": {
      borderColor:
        theme.palette.mode === "dark" ? "rgba(244,63,70,0.32)" : "rgba(244,63,70,0.22)",
      transform: "translateY(-1px)",
    },
  };

  const sectionHeaderSx = {
    color: colors.grey[100],
    fontSize: "16px",
    fontWeight: 900,
    mb: 0.5,
  };

  const mutedTextSx = {
    color: colors.grey[500],
    fontSize: "12px",
    fontWeight: 700,
  };

  const MiniMetric = ({
    title,
    value,
    subtitle,
    icon,
    onClick,
    gridColumn = { xs: "span 12", md: "span 3" },
  }) => (
    <Box
      onClick={onClick}
      sx={{
        ...panelSx,
        cursor: onClick ? "pointer" : "default",
        display: "flex",
        flexDirection: "column",
        gap: 1.25,
        gridColumn,
        minHeight: 136,
        p: 2.25,
      }}
    >
      <Box display="flex" alignItems="center" justifyContent="space-between">
        <Typography sx={mutedTextSx}>{title}</Typography>
        <Box
          sx={{
            alignItems: "center",
            background:
              theme.palette.mode === "dark" ? "rgba(244,63,70,0.12)" : "rgba(244,63,70,0.08)",
            border: `1px solid ${
              theme.palette.mode === "dark" ? "rgba(244,63,70,0.2)" : "rgba(244,63,70,0.14)"
            }`,
            borderRadius: "10px",
            color: colors.primary[100],
            display: "flex",
            height: 36,
            justifyContent: "center",
            width: 36,
          }}
        >
          {icon}
        </Box>
      </Box>
      <Typography variant="h3" sx={{ color: colors.grey[100], lineHeight: 1 }}>
        {value}
      </Typography>
      <Typography sx={mutedTextSx}>{subtitle}</Typography>
    </Box>
  );

  const InfoRow = ({ label, value, meta, onClick }) => (
    <Box
      onClick={onClick}
      sx={{
        alignItems: "center",
        borderBottom: `1px solid ${
          theme.palette.mode === "dark" ? "rgba(255,255,255,0.06)" : "rgba(17,19,24,0.06)"
        }`,
        cursor: onClick ? "pointer" : "default",
        display: "grid",
        gap: 1,
        gridTemplateColumns: "minmax(0, 1fr) auto",
        py: 1.4,
        "&:last-child": {
          borderBottom: 0,
        },
      }}
    >
      <Box minWidth={0}>
        <Typography
          noWrap
          sx={{ color: colors.grey[100], fontSize: "13px", fontWeight: 900 }}
        >
          {label}
        </Typography>
        <Typography noWrap sx={mutedTextSx}>
          {meta}
        </Typography>
      </Box>
      <Typography sx={{ color: colors.primary[100], fontSize: "13px", fontWeight: 900 }}>
        {value}
      </Typography>
    </Box>
  );

  return (
    <Box>
      <Box
        alignItems={{ xs: "flex-start", md: "center" }}
        display="flex"
        flexDirection={{ xs: "column", md: "row" }}
        gap={1.5}
        justifyContent="space-between"
      >
        <Header
          title="Dashboard"
          subtitle="Live loyalty activity across clients, transactions, offers, and program setup."
        />
        <Box display="flex" gap={1} flexWrap="wrap">
          <Chip
            label={`${transactionsData.length} transactions`}
            size="small"
            sx={{
              background:
                theme.palette.mode === "dark" ? "rgba(244,63,70,0.12)" : "rgba(244,63,70,0.08)",
              color: colors.primary[100],
              fontWeight: 800,
            }}
          />
          <Chip
            label={`${dashboardStats.activeOffers}/${offersData.length} active offers`}
            size="small"
            sx={{
              background:
                theme.palette.mode === "dark" ? "rgba(255,255,255,0.05)" : "rgba(17,19,24,0.05)",
              color: colors.grey[400],
              fontWeight: 800,
            }}
          />
        </Box>
      </Box>

      {isLoading ? (
        <Skeleton variant="rectangular" width="100%" height={620} animation="wave" />
      ) : (
        <Box display="grid" gridTemplateColumns="repeat(12, 1fr)" gap="16px">
          <Box
            gridColumn={{ xs: "span 12", xl: "span 8" }}
            minHeight={{ xs: 390, md: 420 }}
            sx={{ ...panelSx, p: { xs: 2, md: 2.5 } }}
          >
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
              <Box>
                <Typography variant="h4" color={colors.grey[100]}>
                  Activity
                </Typography>
                <Typography variant="body2" color={colors.grey[500]} fontWeight={600}>
                  Transactions and bonus points over time
                </Typography>
              </Box>
            </Box>
            <Box height={{ xs: 310, md: 335 }}>
              {lineChartData.length > 0 ? (
                <LineChart
                  data={lineChartData}
                  isDashboard={true}
                  hideAxisLabels={!isNonMobile}
                />
              ) : (
                <Typography variant="h6" color={colors.redAccent[100]}>
                  No transaction data available
                </Typography>
              )}
            </Box>
          </Box>

          <Box
            gridColumn={{ xs: "span 12", xl: "span 4" }}
            sx={{ ...panelSx, p: { xs: 2, md: 2.5 } }}
          >
            <Typography sx={sectionHeaderSx}>Program health</Typography>
            <Typography sx={mutedTextSx}>Quick read on the loyalty setup.</Typography>
            <Box mt={1.5}>
              <InfoRow
                label="Active point value"
                value={`${Number(dashboardStats.activePointValue || 0).toFixed(2)} EUR`}
                meta={`${pointScoresData.length} point rules configured`}
                onClick={() => navigate("/bonuspointsvalue")}
              />
              <InfoRow
                label="Levels"
                value={levelsData.length}
                meta="Reward tiers in the program"
                onClick={() => navigate("/levels")}
              />
              <InfoRow
                label="Customer groups"
                value={customerGroupsData.length}
                meta="Discount and spend segments"
                onClick={() => navigate("/customergroup")}
              />
              <InfoRow
                label="Active offers"
                value={`${dashboardStats.activeOffers}/${offersData.length}`}
                meta="Offers currently inside their date window"
                onClick={() => navigate("/offers")}
              />
            </Box>
          </Box>

          <Box
            gridColumn={{ xs: "span 12", md: "span 6" }}
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            p={{ xs: "18px", md: "22px" }}
            sx={{ ...panelSx, cursor: "pointer" }}
            onClick={() => navigate("/clients")}
          >
            <Box>
              <Typography variant="h2" sx={{ color: colors.grey[100], lineHeight: 1 }}>
                {`${userData.length}/1000`}
              </Typography>
              <Typography variant="h6" sx={{ color: colors.grey[500], mt: 1 }}>
                Client registration goal
              </Typography>
            </Box>
            <ProgressCircle progress={userData.length / 1000} size="60" />
          </Box>

          <MiniMetric
            title="Total revenue"
            value={currencyFormatter.format(dashboardStats.totalAmount)}
            subtitle={`${transactionsData.length} transactions counted`}
            icon={<TrendingUpRoundedIcon />}
            onClick={() => navigate("/transactions")}
          />

          <MiniMetric
            title="Bonus points"
            value={numberFormatter.format(dashboardStats.totalPoints)}
            subtitle="Issued through transactions"
            icon={<StarsRoundedIcon />}
            onClick={() => navigate("/transactions")}
          />

          <Box
            gridColumn={{ xs: "span 12", md: "span 4" }}
            display="flex"
            alignItems="center"
            justifyContent="center"
            sx={{ ...panelSx, cursor: "pointer" }}
            onClick={() => navigate("/clients")}
          >
            <StatBox
              title={userData.length}
              subtitle={`${dashboardStats.activeUsers} active clients`}
              progress={userData.length ? dashboardStats.activeUsers / userData.length : 0}
              icon={<PersonAddIcon sx={{ color: colors.primary[100], fontSize: "24px" }} />}
            />
          </Box>

          <Box
            gridColumn={{ xs: "span 12", md: "span 4" }}
            display="flex"
            alignItems="center"
            justifyContent="center"
            sx={{ ...panelSx, cursor: "pointer" }}
            onClick={() => navigate("/transactions")}
          >
            <StatBox
              title={transactionsData.length}
              subtitle="Transactions"
              progress={Math.min(transactionsData.length / 1000, 1)}
              icon={<PaidIcon sx={{ color: colors.primary[100], fontSize: "24px" }} />}
            />
          </Box>

          <Box
            gridColumn={{ xs: "span 12", md: "span 4" }}
            display="flex"
            alignItems="center"
            justifyContent="center"
            sx={{ ...panelSx, cursor: "pointer" }}
            onClick={() => navigate("/pos")}
          >
            <StatBox
              title={posData.length}
              subtitle="Point of Sales"
              progress={Math.min(posData.length / 50, 1)}
              icon={<PointOfSaleIcon sx={{ color: colors.primary[100], fontSize: "24px" }} />}
            />
          </Box>

          <MiniMetric
            title="Offers"
            value={offersData.length}
            subtitle={`${dashboardStats.activeOffers} active right now`}
            icon={<LocalOfferOutlinedIcon />}
            onClick={() => navigate("/offers")}
            gridColumn={{ xs: "span 12", md: "span 6" }}
          />

          <MiniMetric
            title="Groups"
            value={customerGroupsData.length}
            subtitle={`${levelsData.length} levels configured`}
            icon={<Groups2OutlinedIcon />}
            onClick={() => navigate("/customergroup")}
            gridColumn={{ xs: "span 12", md: "span 6" }}
          />

          <Box
            gridColumn={{ xs: "span 12", lg: "span 7" }}
            sx={{ ...panelSx, p: { xs: 2, md: 2.5 } }}
          >
            <Typography sx={sectionHeaderSx}>Recent transactions</Typography>
            <Typography sx={mutedTextSx}>Latest customer activity from the transactions API.</Typography>
            <Box mt={1.5}>
              {recentTransactions.length > 0 ? (
                recentTransactions.map((transaction, index) => (
                  <InfoRow
                    key={`${transaction.transactionId || transaction.id || index}-${transaction.createDate}`}
                    label={
                      transaction.clientName ||
                      transaction.fullName ||
                      transaction.name ||
                      transaction.phoneNumber ||
                      `Transaction #${transaction.transactionId || transaction.id || index + 1}`
                    }
                    value={currencyFormatter.format(
                      asNumber(
                        transaction.transactionAmount ??
                          transaction.amount ??
                          transaction.totalAmount ??
                          transaction.price
                      )
                    )}
                    meta={`${formatDate(transaction.createDate)} · ${
                      transaction.store ||
                      transaction.storeName ||
                      transaction.pointOfSaleName ||
                      transaction.posName ||
                      "Unknown POS"
                    } · ${asNumber(transaction.bonusPoints)} pts`}
                    onClick={() => navigate("/transactions")}
                  />
                ))
              ) : (
                <Typography sx={{ color: colors.grey[500], fontWeight: 700, py: 2 }}>
                  No recent transactions yet.
                </Typography>
              )}
            </Box>
          </Box>

          <Box
            gridColumn={{ xs: "span 12", lg: "span 5" }}
            sx={{ ...panelSx, p: { xs: 2, md: 2.5 } }}
          >
            <Typography sx={sectionHeaderSx}>Top POS locations</Typography>
            <Typography sx={mutedTextSx}>Ranked by transaction count.</Typography>
            <Box mt={1.5}>
              {topStores.length > 0 ? (
                topStores.map((store) => (
                  <InfoRow
                    key={store.name}
                    label={store.name}
                    value={`${store.count} tx`}
                    meta={`${currencyFormatter.format(store.amount)} · ${numberFormatter.format(
                      store.points
                    )} pts`}
                    onClick={() => navigate("/pos")}
                  />
                ))
              ) : (
                <Typography sx={{ color: colors.grey[500], fontWeight: 700, py: 2 }}>
                  No POS activity yet.
                </Typography>
              )}
            </Box>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default Dashboard;
